import clsx from "clsx";
import exportFromJSON from "export-from-json";
import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form, useField } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDeviceCommand } from "src/providers/DeviceCommandProvider";
import { useDispatch } from "src/store";
import { z } from "zod";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import DropdownContainer from "@dashboard/devices/components/shared/Dropdown/DropdownContainer";
import { DropdownItem } from "@dashboard/devices/components/shared/Dropdown/DropdownItem";
import { BwcConfig, Device } from "@dashboard/devices/types";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import { createDeviceMdmCommand } from "@devices/api/devices";
import { createGroupMdmCommand } from "@devices/api/groups";
import { fetchDevice } from "@devices/store";
import { Box, DialogContentText, Typography, useTheme } from "@mui/material";
import CompareService from "@services/compareService/compareService";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import { NumberField } from "@shared/form/NumberField";
import { ControlledSelectField, SelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { LoadingIcon } from "@shared/Loading";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

import { useStyles } from "./BwcTab.styles";
import { BwcCredentialsFormValues, SetBwcCredentialsDialog } from "./SetBwcCredentialsDialog";

const bwcUpdateDataSchema = z
    .object({
        videoCameraPreference: z
            .object({
                cameraGain: z.number().optional(),
                eisEnabled: z.boolean().optional(),
                isLdcEnabled: z.boolean().optional(),
                noiseReductionMode: z.string().optional(),
                temperatureLimiterEnabled: z.boolean().optional(),
                supportedCameraGains: z.array(z.number()).optional(),
                supportedNoiseReductionModes: z.array(z.string()).optional(),
            })
            .optional(),
        videoRecorderPreference: z
            .object({
                fps: z.string().optional(),
                enabled: z.boolean().optional(),
                videoCodec: z.string().optional(),
                supportedFps: z.array(z.string()).optional(),
                prerecordLength: z.number().optional(),
                videoResolution: z.string().optional(),
                encryptionFormat: z.string().optional(),
                overwriteEnabled: z.boolean().optional(),
                prerecordEnabled: z.boolean().optional(),
                videoBitrateKbps: z.number().optional(),
                supportedVideoCodec: z.array(z.string()).optional(),
                activeRecordDuration: z.string().optional(),
                supportedPreRecordLength: z.array(z.number()).optional(),
                supportedVideoResolution: z.array(z.string()).optional(),
                supportedEncryptionFormat: z.array(z.string()).optional(),
            })
            .optional(),
        videoStreamPreference: z.object({
            fps: z.string().optional(),
            url: z.string().optional(),
            port: z.number().optional(),
            enabled: z.boolean().optional(),
            audioCodec: z.string().optional(),
            videoCodec: z.string().optional(),
            supportedFps: z.array(z.string()).optional(),
            encryptionMode: z.string().optional(),
            videoResolution: z.string().optional(),
            videoBitrateKbps: z.number().optional(),
            encryptionEnabled: z.boolean().optional(),
            streamingProtocol: z.string().optional(),
            supportedVideoCodec: z.array(z.string()).optional(),
            activeStreamDuration: z.string().optional(),
            supportedAudioCodecs: z.array(z.string()).optional(),
            supportedVideoResolution: z.array(z.string()).optional(),
            supportedStreamingProtocol: z.array(z.string()).optional(),
            supportedStreamEncryptionModes: z.array(z.string()).optional(),
        }),
    })
    .optional();

const ComparisonHeader = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common", "modals"]);

    const { input: editingInput } = useField("editing");
    const editing = editingInput?.value;

    return (
        <PropertyRow className={classes.valueHeaderRow}>
            <PropertyKey>&nbsp;</PropertyKey>
            <Box className={classes.valueColumns}>
                {editing ? (
                    <Typography variant="caption" className={classes.columnLabel}>
                        {t("sidepanels:devicePanel.bwcTab.comparison.current")}
                    </Typography>
                ) : null}

                {editing ? (
                    <Typography variant="caption" className={classes.columnLabel}>
                        {t("sidepanels:devicePanel.bwcTab.comparison.imported")}
                    </Typography>
                ) : null}
            </Box>
        </PropertyRow>
    );
};

const defaultSupportedValues = {
    videoCameraPreference: {
        supportedCameraGains: [-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2],
        supportedNoiseReductionModes: ["Off", "Fast", "HighQuality"],
    },
    videoRecorderPreference: {
        supportedFps: ["30fps", "15fps", "10fps", "5fps"],
        supportedVideoCodec: ["H264", "H265"],
        supportedPreRecordLength: [30, 60],
        supportedVideoResolution: ["1920x1080", "1280x720", "854x480"],
        supportedEncryptionFormat: ["AES256"],
    },
    videoStreamPreference: {
        supportedFps: ["30fps", "15fps", "10fps", "5fps"],
        supportedVideoCodec: ["H264", "H265", "MJPEG"],
        supportedAudioCodecs: ["AAC", "PCM A-LAW", "PCM MU-LAW"],
        supportedVideoResolution: ["1920x1080", "1280x720", "854x480"],
        supportedStreamingProtocol: ["RTSP"],
        supportedStreamEncryptionModes: ["NONE", "RTSPS", "SRTP"],
    },
};

function formatLabel(name: string, label: unknown): string {
    if (label === undefined) {
        return "-";
    }

    if (typeof label === "boolean") {
        return label ? i18next.t("common:label.enabled") : i18next.t("common:label.disabled");
    }

    if (name.includes("Bitrate")) {
        return label ? `${label} Kbps` : "-";
    }

    if (typeof label === "number") {
        return String(label);
    }

    if (label && typeof label === "string") {
        return label;
    }

    return "-";
}

type ComparisonRowProps = {
    name: string;
    label: string;
    component: React.ElementType;
    optionsName?: string;
    readonly?: boolean;
};
const ComparisonRow = ({ component, optionsName, readonly, ...props }: React.PropsWithChildren<ComparisonRowProps>) => {
    const { input: oldInput, meta: oldMeta } = useField("old." + props.name);
    const { input: newInput } = useField("new." + props.name);
    const { input: optionsInput } = useField<string[] | number[]>("old." + optionsName);
    const { input: editingInput } = useField("editing");

    const theme = useTheme();
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);

    const fallbackOptions = defaultSupportedValues[props.name as keyof typeof defaultSupportedValues];
    const rawOptions = optionsInput?.value || fallbackOptions || [];
    const options = rawOptions.map((option) => ({ label: String(option), value: option }));

    const hasNewValue = newInput.value !== undefined && newInput.value !== "" && oldInput.value !== newInput.value;
    const wasModified = oldInput.value !== oldMeta.initial;
    const hasChanged = hasNewValue || wasModified;

    const editing = editingInput?.value;

    const Component = component;
    let inputLabel = props.label;

    if (component === ControlledCheckboxField) {
        inputLabel = newInput.value ? t("common:label.enabled") : t("common:label.disabled");
    }

    return (
        <PropertyRow style={{ padding: editing ? `${theme.spacing(1)} 0` : "unset" }}>
            <PropertyKey>{props.label}</PropertyKey>
            <Box className={classes.valueColumns}>
                <PropertyValue className={clsx(classes.valueText, { [classes.changedValue]: hasChanged })}>
                    {formatLabel(props.name, oldInput.value)}
                </PropertyValue>
                {editing && !readonly ? <Component name={"new." + props.name} label={inputLabel} options={options} /> : null}
                {editing && readonly ? <PropertyValue>{formatLabel(props.name, oldInput.value)}</PropertyValue> : null}
            </Box>
        </PropertyRow>
    );
};

const lockOptions = [
    { value: 0, label: i18next.t("sidepanels:devicePanel.bwcTab.deviceLock.unlock") },
    { value: 1, label: i18next.t("sidepanels:devicePanel.bwcTab.deviceLock.lock") },
];

type FormValues = {
    old: BwcConfig;
    new: BwcConfig;
    editing: boolean;
};

type Props = {
    device: Device;
    groupId: number;
    groupName: string;
};

const BwcTab: React.FC<Props> = (props) => {
    const [isRefetchingLoading, setIsRefetchingLoading] = React.useState(false);
    const [isEraseDialogOpen, setIsEraseDialogOpen] = React.useState(false);
    const [isEraseLoading, setIsEraseLoading] = React.useState(false);
    const [isShutdownLoading, setIsShutdownLoading] = React.useState(false);
    const [isUploadLogsLoading, setIsUploadLogsLoading] = React.useState(false);
    const [isShutdownDialogOpen, setIsShutdownDialogOpen] = React.useState(false);
    const [isDeviceLockDialogOpen, setIsDeviceLockDialogOpen] = React.useState(false);
    const [isLockCommandLoading, setIsLockCommandLoading] = React.useState(false);
    const [deviceLockSelectedValue, setDeviceLockSelectedValue] = React.useState(0);
    const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = React.useState(false);
    const [isCredentialsLoading, setIsCredentialsLoading] = React.useState(false);

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const anchorRef = React.useRef<HTMLButtonElement | null>(null);
    const { t } = useTranslation(["sidepanels", "common", "modals"]);
    const theme = useTheme();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const commandsContext = useDeviceCommand();

    const bwcData = props.device.lastState?.extra?.media;
    const videoCameraPreference = bwcData?.videoCameraPreference;
    const videoRecorderPreference = bwcData?.videoRecorderPreference;
    const videoStreamPreference = bwcData?.videoStreamPreference;

    const hasBwcData = Boolean(videoCameraPreference && videoRecorderPreference && videoStreamPreference);

    const initialValues = React.useMemo(() => ({ old: bwcData, new: bwcData, editing: false }), [props.device]);

    const handleImport = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, form: FormApi<FormValues>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        event.target.value = "";

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const fileContent = e.target?.result?.toString();
                if (!fileContent) {
                    showErrorToast(t("sidepanels:devicePanel.bwcTab.toast.fileReadError"));
                    return;
                }

                const parsedData = JSON.parse(fileContent);
                const validatedData = bwcUpdateDataSchema.parse(parsedData) as Partial<BwcConfig>;

                form.batch(() => {
                    const oldData = form.getFieldState("old")?.value;
                    const newData = _.merge(_.cloneDeep(oldData), validatedData);
                    form.change("new", newData);
                    form.change("editing", true);
                });
                showSuccessToast(t("sidepanels:devicePanel.bwcTab.toast.importSuccess"));
            } catch (error) {
                if (error instanceof z.ZodError) {
                    showErrorToast(t("sidepanels:devicePanel.bwcTab.toast.importInvalidFormat"));
                } else {
                    showErrorToast(t("sidepanels:devicePanel.bwcTab.toast.importParseError"));
                }
            }
        };

        reader.onerror = () => {
            showErrorToast(t("sidepanels:devicePanel.bwcTab.toast.fileReadError"));
        };

        reader.readAsText(file);
    };

    const handleExport = () => {
        try {
            if (!videoCameraPreference || !videoRecorderPreference || !videoStreamPreference) {
                showErrorToast(t("sidepanels:devicePanel.bwcTab.toast.exportError"));
                return;
            }

            const exportData: BwcConfig = {
                videoCameraPreference,
                videoRecorderPreference,
                videoStreamPreference,
            };

            exportFromJSON({
                data: exportData,
                fileName: `bwc-config-${new Date().toISOString().split("T")[0]}`,
                exportType: exportFromJSON.types.json,
            });

            showSuccessToast(t("sidepanels:devicePanel.bwcTab.toast.exportSuccess"));
        } catch (_error) {
            showErrorToast(t("sidepanels:devicePanel.bwcTab.toast.exportError"));
        }
    };

    const handleUploadLogs = async () => {
        try {
            setIsUploadLogsLoading(true);
            await createDeviceMdmCommand({
                deviceId: props.device.id,
                json: {
                    command: "sendBroadcast",
                    caption: "Upload event logs",
                    intent: {
                        component: "com.jkc.servicehub/.logs.LogUploadReceiver",
                        action: "com.jkc.servicehub.UPLOAD_LOGS",
                    },
                },
            });
            showSuccessToast("Logs upload command sent successfully");
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || "Failed to send logs upload command. Please try again.");
        } finally {
            setIsUploadLogsLoading(false);
        }
    };

    const handleErase = async () => {
        try {
            setIsEraseLoading(true);
            await createDeviceMdmCommand({
                deviceId: props.device.id,
                json: {
                    caption: "Erase Recordings",
                    command: "queryContentProvider",
                    query: {
                        name: "eraseRecordings",
                        uri: "content://com.jkc.httpcontentprovider.localserver/post?path=delete-recorded-files",
                        type: "String",
                    },
                },
            });
            showSuccessToast(t("sidepanels:devicePanel.bwcTab.toast.eraseSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.bwcTab.toast.eraseError"));
        } finally {
            setIsEraseLoading(false);
        }
    };

    const handleShutdown = async () => {
        try {
            setIsShutdownLoading(true);
            await createDeviceMdmCommand({ deviceId: props.device.id, json: { command: "shutdownDevice" } });
            showSuccessToast(t("sidepanels:devicePanel.bwcTab.toast.shutdownSuccess"));
            setIsShutdownDialogOpen(false);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.bwcTab.toast.shutdownError"));
        } finally {
            setIsShutdownLoading(false);
        }
    };

    const handleShutdownClick = () => {
        setIsShutdownDialogOpen(true);
    };

    const handleSetCredentials = async (values: BwcCredentialsFormValues) => {
        try {
            setIsCredentialsLoading(true);
            const body = encodeURIComponent(JSON.stringify({ username: values.bwcAccount, password: values.bwcSecret }));
            const json = {
                caption: "Set BWC credentials",
                command: "queryContentProvider",
                query: {
                    name: "setBwcCredentials",
                    uri: `content://com.jkc.httpcontentprovider.localserver/post?path=change-credentials&body=${body}`,
                    type: "String",
                },
            };
            if (values.applyToGroup) {
                // The backend queues one command per device it resolved, so the response length is
                // how many devices were actually reached.
                const commands = await createGroupMdmCommand({ groupId: props.groupId, json });
                showSuccessToast(
                    t("sidepanels:devicePanel.bwcTab.toast.credentialsGroupSuccess", {
                        count: commands.length,
                        groupName: props.groupName,
                    }),
                );
            } else {
                await createDeviceMdmCommand({ deviceId: props.device.id, json });
                showSuccessToast(t("sidepanels:devicePanel.bwcTab.toast.credentialsSuccess"));
            }
            setIsCredentialsDialogOpen(false);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.bwcTab.toast.credentialsError"));
        } finally {
            setIsCredentialsLoading(false);
        }
    };

    const handleDeviceLockClick = () => {
        setDeviceLockSelectedValue(0);
        setIsDeviceLockDialogOpen(true);
    };

    const handleDeviceLockChange = async () => {
        try {
            setIsLockCommandLoading(true);
            const body = encodeURIComponent(JSON.stringify({ stateValue: deviceLockSelectedValue }));
            await commandsContext.onCreate(
                {
                    caption: "Change device lock state",
                    command: "queryContentProvider",
                    query: {
                        name: "deviceLockState",
                        uri: `content://com.jkc.httpcontentprovider.localserver/post?path=device-lock-state&body=${body}`,
                        type: "String",
                    },
                },
                null,
            );
            setIsDeviceLockDialogOpen(false);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.bwcTab.toast.deviceLockError"));
        } finally {
            setIsLockCommandLoading(false);
        }
    };

    const onCancel = (form: FormApi<FormValues>) => {
        form.reset(initialValues);
    };

    const onEdit = (form: FormApi<FormValues>) => {
        form.change("editing", true);
    };

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const payload = new CompareService(values.old, values.new).getDifferencesObject();
        const body = encodeURIComponent(JSON.stringify(payload));
        await commandsContext.onCreate(
            {
                command: "queryContentProvider",
                caption: "Change camera setting",
                query: {
                    name: "newCameraSettings",
                    uri: `content://com.jkc.httpcontentprovider.localserver/post?path=bwc-media-preference&body=${body}`,
                    type: "String",
                },
            },
            null,
        );
        form.batch(() => {
            const mergedValues = _.merge(_.cloneDeep(values.old), values.new);
            form.change("old", mergedValues);
            form.change("editing", false);
        });
    };

    const refetchDevice = async () => {
        try {
            setIsRefetchingLoading(true);
            await dispatch(fetchDevice({ deviceId: props.device.id })).unwrap();
            setIsRefetchingLoading(false);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.bwcTab.toast.refetchError"));
        } finally {
            setIsRefetchingLoading(false);
        }
    };

    const prevIsLastCommandPending = React.useRef<boolean>(false);

    const updateCommands = commandsContext.data.filter((command) => command.payload.command === "queryContentProvider");
    const sortedCommands = _.orderBy(updateCommands, "created", "desc");
    const lastCommand = sortedCommands[0];

    const isLastCommandPending = lastCommand && lastCommand?.status !== "successful" && lastCommand?.status !== "ignored";

    React.useEffect(() => {
        if (prevIsLastCommandPending.current && !isLastCommandPending) {
            refetchDevice();
        }
        prevIsLastCommandPending.current = isLastCommandPending;
    }, [isLastCommandPending, prevIsLastCommandPending]);

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ handleSubmit, values, form }) => (
                <form onSubmit={handleSubmit}>
                    <PaneElement.Container>
                        <Box className={classes.menuWrapper}>
                            <DropdownContainer icon={<i className="fas fa-ellipsis-v" />} anchorRef={anchorRef}>
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.editSettings")}
                                    iconName="fa-solid fa-fw fa-edit"
                                    onClick={() => onEdit(form)}
                                    disabled={!hasBwcData}
                                    tooltipProps={{
                                        hide: hasBwcData,
                                        title: !hasBwcData ? t("sidepanels:devicePanel.bwcTab.tooltip.noBwcSettings") : "",
                                    }}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.exportSettings")}
                                    iconName="fa-solid fa-fw fa-download"
                                    onClick={handleExport}
                                    disabled={!hasBwcData}
                                    tooltipProps={{
                                        hide: hasBwcData,
                                        title: !hasBwcData ? t("sidepanels:devicePanel.bwcTab.tooltip.noBwcSettings") : "",
                                    }}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.importSettings")}
                                    iconName="fa-solid fa-fw fa-upload"
                                    onClick={handleImport}
                                    disabled={!hasBwcData}
                                    tooltipProps={{
                                        hide: hasBwcData,
                                        title: !hasBwcData ? t("sidepanels:devicePanel.bwcTab.tooltip.noBwcSettings") : "",
                                    }}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.uploadLogs")}
                                    disabled={isUploadLogsLoading}
                                    iconName="fa-solid fa-fw fa-file-arrow-up"
                                    onClick={handleUploadLogs}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.eraseRecordings")}
                                    disabled={isEraseLoading}
                                    iconName="fa-solid fa-fw fa-eraser"
                                    onClick={() => setIsEraseDialogOpen(true)}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.setCredentials")}
                                    iconName="fa-solid fa-fw fa-key"
                                    onClick={() => setIsCredentialsDialogOpen(true)}
                                    disabled={isCredentialsLoading}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.changeDeviceLock")}
                                    iconName="fa-solid fa-fw fa-lock"
                                    onClick={handleDeviceLockClick}
                                    disabled={isLockCommandLoading}
                                />
                                <DropdownItem
                                    label={t("sidepanels:devicePanel.bwcTab.button.shutdownDevice")}
                                    iconName="fa-solid fa-fw fa-power-off"
                                    iconProps={{ style: { color: theme.palette.emteria.error } }}
                                    labelProps={{ color: theme.palette.emteria.error }}
                                    onClick={handleShutdownClick}
                                    disabled={isShutdownLoading || !props.device.isOnline}
                                    tooltipProps={{
                                        hide: props.device.isOnline,
                                        title: !props.device.isOnline
                                            ? i18next.t("sidepanels:devicePanel.bwcTab.shutdown.tooltip.offline")
                                            : "",
                                    }}
                                />
                            </DropdownContainer>
                        </Box>
                        <PaneElement.Section>
                            <Box className={classes.streamingContainer}>
                                <Box className={classes.collapsibleWrapper}>
                                    <CollapsibleProvider initialValue={true}>
                                        <CollapsibleSectionSummary>
                                            <CollapsibleSectionTitle size="medium" bold>
                                                {t("sidepanels:devicePanel.bwcTab.camera.title")}
                                            </CollapsibleSectionTitle>
                                        </CollapsibleSectionSummary>
                                        <CollapsibleSectionDetails>
                                            <ComparisonHeader />
                                            <ComparisonRow
                                                name="videoCameraPreference.cameraGain"
                                                label={t("sidepanels:devicePanel.bwcTab.camera.cameraGain")}
                                                component={ControlledSelectField}
                                                optionsName="videoCameraPreference.supportedCameraGains"
                                            />
                                            <ComparisonRow
                                                name="videoCameraPreference.eisEnabled"
                                                label={t("sidepanels:devicePanel.bwcTab.camera.eisEnabled")}
                                                component={ControlledCheckboxField}
                                            />
                                            <ComparisonRow
                                                name="videoCameraPreference.isLdcEnabled"
                                                label={t("sidepanels:devicePanel.bwcTab.camera.isLdcEnabled")}
                                                component={ControlledCheckboxField}
                                            />
                                            <ComparisonRow
                                                name="videoCameraPreference.noiseReductionMode"
                                                label={t("sidepanels:devicePanel.bwcTab.camera.noiseReductionMode")}
                                                component={ControlledSelectField}
                                                optionsName="videoCameraPreference.supportedNoiseReductionModes"
                                            />
                                            <ComparisonRow
                                                name="videoCameraPreference.temperatureLimiterEnabled"
                                                label={t("sidepanels:devicePanel.bwcTab.camera.temperatureLimiterEnabled")}
                                                component={ControlledCheckboxField}
                                            />
                                        </CollapsibleSectionDetails>
                                    </CollapsibleProvider>
                                </Box>
                            </Box>
                        </PaneElement.Section>

                        <PaneElement.Section>
                            <CollapsibleProvider initialValue={true}>
                                <CollapsibleSectionSummary>
                                    <CollapsibleSectionTitle size="medium" bold>
                                        {t("sidepanels:devicePanel.bwcTab.streaming.title")}
                                    </CollapsibleSectionTitle>
                                </CollapsibleSectionSummary>
                                <CollapsibleSectionDetails>
                                    <ComparisonHeader />
                                    <ComparisonRow
                                        name="videoStreamPreference.enabled"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.status")}
                                        component={ControlledCheckboxField}
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.fps"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.fps")}
                                        component={ControlledSelectField}
                                        optionsName="videoStreamPreference.supportedFps"
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.url"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.rtspUrl")}
                                        component={TextField}
                                        readonly
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.streamingProtocol"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.protocol")}
                                        component={ControlledSelectField}
                                        optionsName="videoStreamPreference.supportedStreamingProtocol"
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.videoCodec"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.encodeFormat")}
                                        component={ControlledSelectField}
                                        optionsName="videoStreamPreference.supportedVideoCodec"
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.videoResolution"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.resolution")}
                                        component={ControlledSelectField}
                                        optionsName="videoStreamPreference.supportedVideoResolution"
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.port"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.port")}
                                        component={TextField}
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.encryptionEnabled"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.encryption")}
                                        component={ControlledCheckboxField}
                                        readonly
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.activeStreamDuration"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.activeStreamDuration")}
                                        component={TextField}
                                        readonly
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.audioCodec"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.audioCodec")}
                                        component={TextField}
                                        readonly
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.encryptionMode"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.encryptionMode")}
                                        component={ControlledSelectField}
                                        optionsName="videoStreamPreference.supportedStreamEncryptionModes"
                                    />
                                    <ComparisonRow
                                        name="videoStreamPreference.videoBitrateKbps"
                                        label={t("sidepanels:devicePanel.bwcTab.streaming.videoBitrate")}
                                        component={NumberField}
                                    />
                                </CollapsibleSectionDetails>
                            </CollapsibleProvider>
                        </PaneElement.Section>

                        <PaneElement.Section withoutBorder>
                            <CollapsibleProvider initialValue={true}>
                                <CollapsibleSectionSummary>
                                    <CollapsibleSectionTitle size="medium" bold>
                                        {t("sidepanels:devicePanel.bwcTab.recording.title")}
                                    </CollapsibleSectionTitle>
                                </CollapsibleSectionSummary>
                                <CollapsibleSectionDetails>
                                    <ComparisonHeader />
                                    <ComparisonRow
                                        name="videoRecorderPreference.enabled"
                                        label={t("sidepanels:devicePanel.bwcTab.recording.status")}
                                        component={ControlledCheckboxField}
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.videoResolution"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.resolution")}
                                        component={ControlledSelectField}
                                        optionsName="videoRecorderPreference.supportedVideoResolution"
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.fps"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.frameRate")}
                                        component={ControlledSelectField}
                                        optionsName="videoRecorderPreference.supportedFps"
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.videoCodec"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.recordingEncode")}
                                        component={ControlledSelectField}
                                        optionsName="videoRecorderPreference.supportedVideoCodec"
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.videoBitrateKbps"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.maxBitrate")}
                                        component={NumberField}
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.encryptionFormat"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.encryptionFormat")}
                                        component={ControlledSelectField}
                                        optionsName="videoRecorderPreference.supportedEncryptionFormat"
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.activeRecordDuration"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.activeRecordDuration")}
                                        component={TextField}
                                        readonly
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.prerecordEnabled"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.prerecording")}
                                        component={ControlledCheckboxField}
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.overwriteEnabled"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.overwriteRecording")}
                                        component={ControlledCheckboxField}
                                    />
                                    <ComparisonRow
                                        name="videoRecorderPreference.prerecordLength"
                                        label={t("sidepanels:devicePanel.bwcTab.cameraInfo.prerecordLength")}
                                        component={ControlledSelectField}
                                        optionsName="videoRecorderPreference.supportedPreRecordLength"
                                    />
                                </CollapsibleSectionDetails>
                            </CollapsibleProvider>
                        </PaneElement.Section>

                        {values?.editing ? (
                            <Box className={classes.buttonContainer}>
                                <SecondaryButton onClick={() => onCancel(form)}>{t("common:button.cancel")}</SecondaryButton>
                                <PrimaryButton type="submit" startIcon={<i className="fas fa-save" />}>
                                    {t("common:button.save")}
                                </PrimaryButton>
                            </Box>
                        ) : null}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json,application/json"
                            style={{ display: "none" }}
                            onChange={(event) => handleFileChange(event, form)}
                        />

                        <ConfirmDialog
                            open={isEraseDialogOpen}
                            title={i18next.t("modals:eraseRecordings.caption")}
                            onConfirm={handleErase}
                            onClose={() => setIsEraseDialogOpen(false)}
                            dangerButton
                            primaryActionText={t("sidepanels:devicePanel.bwcTab.button.eraseRecordings")}
                        >
                            <DialogContentText>
                                <Trans
                                    ns="modals"
                                    i18nKey="eraseRecordings.description"
                                    components={{
                                        bold: <strong />,
                                    }}
                                />
                            </DialogContentText>
                        </ConfirmDialog>

                        <ConfirmDialog
                            title={i18next.t("modals:bwcTab.shutdown.caption")}
                            open={isShutdownDialogOpen}
                            onConfirm={handleShutdown}
                            onClose={() => setIsShutdownDialogOpen(false)}
                            dangerButton
                            primaryActionText={t("sidepanels:devicePanel.bwcTab.button.shutdownDevice")}
                        >
                            <DialogContentText>
                                <Trans
                                    ns="modals"
                                    i18nKey="bwcTab.shutdown.description"
                                    components={{
                                        bold: <strong />,
                                    }}
                                />
                            </DialogContentText>
                        </ConfirmDialog>

                        <Modal
                            open={isDeviceLockDialogOpen}
                            onClose={() => !isLockCommandLoading && setIsDeviceLockDialogOpen(false)}
                            title={t("sidepanels:devicePanel.bwcTab.deviceLock.title")}
                            description={t("sidepanels:devicePanel.bwcTab.deviceLock.description")}
                            secondaryActionText={t("common:button.cancel")}
                            endButton={
                                <PrimaryButton onClick={handleDeviceLockChange} disabled={isLockCommandLoading}>
                                    {t("common:button.confirm")}
                                </PrimaryButton>
                            }
                        >
                            <SelectField
                                label={t("sidepanels:devicePanel.bwcTab.deviceLock.selectLabel")}
                                options={lockOptions}
                                value={lockOptions.find((option) => option.value === deviceLockSelectedValue)}
                                onChange={(_event, selectedOption) => {
                                    if (selectedOption && typeof selectedOption.value === "number") {
                                        setDeviceLockSelectedValue(selectedOption.value);
                                    }
                                }}
                                fullWidth
                                disableSorting
                            />
                            {!props.device.isOnline ? (
                                <CustomAlert severity="warning">
                                    {t("sidepanels:devicePanel.bwcTab.deviceLock.offlineWarning")}
                                </CustomAlert>
                            ) : null}
                        </Modal>

                        <SetBwcCredentialsDialog
                            open={isCredentialsDialogOpen}
                            loading={isCredentialsLoading}
                            groupName={props.groupName}
                            onClose={() => setIsCredentialsDialogOpen(false)}
                            onSubmit={handleSetCredentials}
                        />

                        {isLastCommandPending || isRefetchingLoading ? (
                            <Box className={classes.loaderContainer}>
                                <LoadingIcon />
                                <Typography>{t("sidepanels:devicePanel.bwcTab.pending")}</Typography>
                            </Box>
                        ) : null}
                    </PaneElement.Container>
                </form>
            )}
        />
    );
};

export default BwcTab;
